import { isFreeJumpOpen } from './shared-responder-v2-core-reachability.helpers';
import { RESPONDER_PHASES } from './shared-responder-v2-core-session.consts';
import type { ResponderSessionState } from './shared-responder-v2-core-session.types';
import { getThreadBehaviour } from './shared-responder-v2-core-thread-registry';
import {
  THREAD_STATUSES,
  TURN_INTENTS,
  TURN_ROLES,
  type ThreadStatus,
} from './shared-responder-v2-core-thread.consts';
import type { ResponderTurn } from './shared-responder-v2-core-thread.types';

/** Where a reloaded session picks up, read off each step's resumed completion. */
export interface ResumedSessionCursor {
  readonly stepIndex: number;
  readonly isComplete: boolean;
}

/** Everything the resume read learned, in step order. */
export interface ResumedSessionWalk {
  /** Per step, whether its backend history shows a finished answer. */
  readonly completions: readonly boolean[];
  /** `state.steps` question ids, index-aligned with `completions`. */
  readonly stepQuestionIds: readonly string[];
  /** `surveyCompletionStatus.missingRequiredQuestions`; `[]` when unknown. */
  readonly missingRequiredQuestionIds: readonly string[];
}

/**
 * Where a reloaded session picks up: one step past the furthest question the
 * backend has an answer for.
 *
 * Deliberately NOT "the first step with no answer". Branching means the step
 * list is a superset of the path actually walked — a rule can route straight
 * from question 1 to question 10, and questions 2–9 are then *skipped*, not
 * pending. Their histories are empty for exactly the same reason an unreached
 * question's is, so the first-unanswered scan stopped at question 2 and dropped
 * a responder who was really on question 10 back to the start of a branch they
 * had already been routed around. Answering there is refused
 * (`SURVEY_MESSAGES_V2_JUMP_NOT_ALLOWED`) because the backend's cursor never
 * moved back with them.
 *
 * The backend's own `missingRequiredQuestions` is the only authority on whether
 * anything is genuinely outstanding, so it — not the local scan — decides
 * completion. An empty survey, or one with no answers at all, resumes at the
 * start: "nothing answered" must never render the thank-you screen.
 */
export function resolveResumedSessionCursor(
  walk: ResumedSessionWalk
): ResumedSessionCursor {
  const { completions, missingRequiredQuestionIds, stepQuestionIds } = walk;
  const lastAnswered = completions.lastIndexOf(true);

  if (completions.length === 0 || lastAnswered === -1) {
    return { stepIndex: 0, isComplete: false };
  }

  const missingRequired = new Set(missingRequiredQuestionIds);
  const isMissingAnything = stepQuestionIds.some((questionId) =>
    missingRequired.has(questionId)
  );

  if (lastAnswered < completions.length - 1) {
    return { stepIndex: lastAnswered + 1, isComplete: false };
  }

  return { stepIndex: lastAnswered, isComplete: !isMissingAnything };
}

function resolveResumedSkippedQuestionIds(
  state: ResponderSessionState,
  resumedStepIndex: number
): readonly string[] {
  if (isFreeJumpOpen(state.capabilities)) {
    return state.skippedQuestionIds;
  }

  const stepsBelowCursor = state.steps
    .slice(0, resumedStepIndex)
    .map((step) => step.questionId)
    .filter((questionId) => !state.skippedQuestionIds.includes(questionId));

  return [...state.skippedQuestionIds, ...stepsBelowCursor];
}

const RESUMABLE_PHASES: ReadonlySet<string> = new Set([
  RESPONDER_PHASES.Intro,
  RESPONDER_PHASES.Question,
]);

const FINISHED_THREAD_STATUSES: ReadonlySet<ThreadStatus> = new Set([
  THREAD_STATUSES.Answered,
  THREAD_STATUSES.Closed,
]);

function resolveResumedSiteUrl(
  state: ResponderSessionState,
  stepIndex: number
): string | null {
  for (let index = stepIndex - 1; index >= 0; index -= 1) {
    const questionId = state.steps[index]?.questionId;
    const spec = questionId === undefined ? undefined : state.specs[questionId];
    const thread =
      questionId === undefined ? undefined : state.threads[questionId];
    const url = spec?.contentUrl?.trim() ?? '';

    if (
      spec !== undefined &&
      thread !== undefined &&
      FINISHED_THREAD_STATUSES.has(thread.status) &&
      getThreadBehaviour(spec.kind).ownsContentUrl &&
      url !== ''
    ) {
      return url;
    }
  }

  return null;
}

/**
 * Moves a freshly built session onto the progress its backend history proves.
 *
 * Only ever forwards: a resume that lands behind where the responder already is
 * would undo work done while the read was in flight. A survey the backend has
 * already finished goes straight to the outro rather than reopening its last
 * question, which is what stops a returning link from restarting a completed
 * response.
 */
export function applySessionResume(
  state: ResponderSessionState,
  cursor: ResumedSessionCursor
): ResponderSessionState {
  if (!RESUMABLE_PHASES.has(state.phase)) {
    return state;
  }

  if (cursor.isComplete) {
    return {
      ...state,
      isComplete: true,
      phase: RESPONDER_PHASES.Outro,
    };
  }

  if (
    cursor.stepIndex <= state.currentStepIndex ||
    cursor.stepIndex > state.steps.length - 1
  ) {
    return state;
  }

  return {
    ...state,
    phase: RESPONDER_PHASES.Question,
    currentStepIndex: cursor.stepIndex,
    maxReachedStepIndex: Math.max(state.maxReachedStepIndex, cursor.stepIndex),
    lastOpenedSiteUrl:
      state.lastOpenedSiteUrl ?? resolveResumedSiteUrl(state, cursor.stepIndex),
    resumeStepIndex: Math.max(state.resumeStepIndex, cursor.stepIndex),
    skippedQuestionIds: resolveResumedSkippedQuestionIds(
      state,
      cursor.stepIndex
    ),
  };
}

/**
 * Whether a question resumed from the backend's message history is already
 * answered.
 *
 * The resume read returns messages and nothing else — the backend removed the
 * call that used to report a question session's status alongside them. Rather
 * than treat every resumed question as unanswered (which would invite a second
 * answer to a question the responder already finished), completion is derived
 * from the history itself, which is the same evidence the responder sees on
 * screen:
 *
 * - no message of their own yet ⇒ unanswered;
 * - their last exchange ended on an AI follow-up ⇒ still open, that follow-up
 *   is waiting for a reply;
 * - otherwise ⇒ answered.
 *
 * A wrong guess is recoverable in one direction only, which is why it leans
 * this way: the next turn's `isQuestionComplete` corrects an over-eager `true`,
 * while a spurious `false` would let the responder answer twice.
 */
export function resolveResumedThreadCompletion(
  turns: readonly ResponderTurn[],
  hasUnrenderedResponderTurn = false
): boolean {
  const hasResponderTurn =
    hasUnrenderedResponderTurn ||
    turns.some((turn) => turn.role === TURN_ROLES.Responder);

  if (!hasResponderTurn) {
    return false;
  }

  const lastTurn = turns.at(-1);

  return !(
    lastTurn?.role === TURN_ROLES.Assistant &&
    lastTurn.intent === TURN_INTENTS.FollowUpQuestion
  );
}
